/**
 * Error Monitoring Hook
 *
 * Provides React hooks for comprehensive error monitoring including:
 * - JavaScript errors and exceptions
 * - Promise rejections
 * - Component lifecycle errors
 * - Network errors
 * - Custom error tracking
 */

import { useEffect, useRef, useCallback } from 'react';

export interface ErrorEvent {
  type: 'error' | 'unhandledrejection' | 'network' | 'custom';
  message: string;
  stack?: string;
  url?: string;
  line?: number;
  column?: number;
  error?: Error;
  timestamp: number;
  userAgent: string;
  pathname: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
  tags?: string[];
}

export interface ErrorStats {
  totalErrors: number;
  bySeverity: Record<string, number>;
  byType: Record<string, number>;
  lastError?: ErrorEvent;
  firstErrorTime?: number;
}

type ErrorHandler = (errorEvent: ErrorEvent) => void;

const errorHandlers: ErrorHandler[] = [];
const errorStack: ErrorEvent[] = [];
const MAX_ERROR_STACK = 50;

let isMonitoringActive = false;

/**
 * Register a callback for error events
 */
export function onError(handler: ErrorHandler): void {
  errorHandlers.push(handler);
}

/**
 * Report error to all registered handlers
 */
function reportError(errorEvent: ErrorEvent): void {
  errorHandlers.forEach((handler) => {
    try {
      handler(errorEvent);
    } catch (error) {
      console.error('[ErrorMonitoring] Error in handler:', error);
    }
  });
}

/**
 * Add error to stack (with max size limit)
 */
function addToErrorStack(errorEvent: ErrorEvent): void {
  errorStack.push(errorEvent);
  if (errorStack.length > MAX_ERROR_STACK) {
    errorStack.shift();
  }
}

/**
 * Determine error severity
 */
function determineSeverity(
  type: string,
  message: string
): 'low' | 'medium' | 'high' | 'critical' {
  // Network errors
  if (type === 'network' || message.includes('fetch') || message.includes('XMLHttpRequest')) {
    return 'medium';
  }

  // Critical errors
  if (
    message.includes('Cannot read') ||
    message.includes('undefined is not') ||
    message.includes('ReferenceError')
  ) {
    return 'high';
  }

  // Unhandled rejections are critical
  if (type === 'unhandledrejection') {
    return 'critical';
  }

  return 'low';
}

/**
 * Global error handler setup
 */
export function setupGlobalErrorHandlers(): void {
  if (isMonitoringActive) {
    return;
  }

  isMonitoringActive = true;

  // Handle uncaught errors
  window.addEventListener('error', (event: Event) => {
    const errorEvent = event as any;
    const errorEventData: ErrorEvent = {
      type: 'error',
      message: errorEvent.message || '',
      stack: errorEvent.error?.stack,
      url: errorEvent.filename,
      line: errorEvent.lineno,
      column: errorEvent.colno,
      error: errorEvent.error,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      pathname: window.location.pathname,
      severity: determineSeverity('error', errorEvent.message || ''),
    };

    addToErrorStack(errorEventData);
    reportError(errorEventData);
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: Event) => {
    const rejectionEvent = event as any;
    const errorMsg = rejectionEvent.reason?.message || String(rejectionEvent.reason);
    const errorEventData: ErrorEvent = {
      type: 'unhandledrejection',
      message: `Unhandled Promise Rejection: ${errorMsg}`,
      stack: rejectionEvent.reason?.stack,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      pathname: window.location.pathname,
      severity: 'critical',
      error: rejectionEvent.reason instanceof Error ? rejectionEvent.reason : new Error(errorMsg),
    };

    addToErrorStack(errorEventData);
    reportError(errorEventData);
  });

  if (process.env.NODE_ENV === 'development') {
    console.debug('[ErrorMonitoring] Global error handlers setup complete');
  }
}

/**
 * useErrorMonitoring Hook
 *
 * Provides error monitoring within React components with automatic cleanup
 *
 * @example
 * function MyComponent() {
 *   const { captureError, captureException } = useErrorMonitoring('MyComponent');
 *
 *   const handleAction = async () => {
 *     try {
 *       await someAsync();
 *     } catch (error) {
 *       captureException(error);
 *     }
 *   };
 *
 *   return <button onClick={handleAction}>Action</button>;
 * }
 */
export function useErrorMonitoring(componentName: string) {
  const handlersRef = useRef<Array<() => void>>([]);

  /**
   * Capture and report an error
   */
  const captureError = useCallback(
    (
      message: string,
      severity: 'low' | 'medium' | 'high' | 'critical' = 'medium',
      context?: Record<string, any>
    ): void => {
      const errorEvent: ErrorEvent = {
        type: 'custom',
        message,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
        severity,
        tags: [componentName],
        context: {
          component: componentName,
          ...context,
        },
      };

      addToErrorStack(errorEvent);
      reportError(errorEvent);
    },
    [componentName]
  );

  /**
   * Capture an exception/error object
   */
  const captureException = useCallback(
    (error: Error | unknown, context?: Record<string, any>, severity?: 'low' | 'medium' | 'high' | 'critical'): void => {
      const isError = error instanceof Error;
      const errorEvent: ErrorEvent = {
        type: 'custom',
        message: isError ? error.message : String(error),
        stack: isError ? error.stack : undefined,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
        severity: severity || determineSeverity('error', isError ? error.message : String(error)),
        error: isError ? error : new Error(String(error)),
        tags: [componentName],
        context: {
          component: componentName,
          ...context,
        },
      };

      addToErrorStack(errorEvent);
      reportError(errorEvent);
    },
    [componentName]
  );

  /**
   * Wrap an async function with error handling
   */
  const wrapAsync = useCallback(
    <T extends any[], R>(fn: (...args: T) => Promise<R>) =>
      async (...args: T): Promise<R | null> => {
        try {
          return await fn(...args);
        } catch (error) {
          captureException(error, {
            function: fn.name || 'anonymous',
            args: args.length,
          });
          return null;
        }
      },
    [captureException]
  );

  /**
   * Wrap a sync function with error handling
   */
  const wrapSync = useCallback(
    <T extends any[], R>(fn: (...args: T) => R) =>
      (...args: T): R | null => {
        try {
          return fn(...args);
        } catch (error) {
          captureException(error, {
            function: fn.name || 'anonymous',
            args: args.length,
          });
          return null;
        }
      },
    [captureException]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handlersRef.current.forEach((cleanup) => cleanup());
      handlersRef.current = [];
    };
  }, []);

  return {
    captureError,
    captureException,
    wrapAsync,
    wrapSync,
  };
}

/**
 * useErrorBoundary Hook (if using Error Boundaries)
 *
 * Captures errors in Error Boundary components
 */
export function useErrorBoundary(componentName: string) {
  const handlersRef = useRef<Array<() => void>>([]);

  const captureErrorBoundary = useCallback(
    (error: Error, errorInfo: Record<string, any>): void => {
      const errorEvent: ErrorEvent = {
        type: 'error',
        message: `Error Boundary caught: ${error.message}`,
        stack: error.stack,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        pathname: window.location.pathname,
        severity: 'high',
        error,
        tags: [componentName, 'error-boundary'],
        context: {
          component: componentName,
          componentStack: errorInfo.componentStack,
        },
      };

      addToErrorStack(errorEvent);
      reportError(errorEvent);
    },
    [componentName]
  );

  useEffect(() => {
    return () => {
      handlersRef.current.forEach((cleanup) => cleanup());
      handlersRef.current = [];
    };
  }, []);

  return {
    captureErrorBoundary,
  };
}

/**
 * Setup network error monitoring
 */
export function monitorNetworkErrors(): void {
  const shouldIgnoreNetworkError = (response: Response): boolean => {
    if (process.env.NODE_ENV !== 'development') return false;
    if (response.status !== 404) return false;
    return response.url.includes('/api/public/congregation/check-phone');
  };

  // Intercept fetch errors
  const originalFetch = window.fetch;
  window.fetch = function (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    return originalFetch(input, init)
      .then((response) => {
        if (shouldIgnoreNetworkError(response)) {
          return response;
        }
        if (!response.ok) {
          const errorEvent: ErrorEvent = {
            type: 'network',
            message: `Network Error: ${response.status} ${response.statusText}`,
            url: response.url,
            timestamp: Date.now(),
            userAgent: navigator.userAgent,
            pathname: window.location.pathname,
            severity: response.status >= 500 ? 'high' : 'medium',
            context: {
              status: response.status,
              statusText: response.statusText,
              url: response.url,
              method: init?.method || 'GET',
            },
          };

          addToErrorStack(errorEvent);
          reportError(errorEvent);
        }
        return response;
      })
      .catch((error) => {
        const inputStr = input instanceof Request ? input.url : String(input);
        const errorEvent: ErrorEvent = {
          type: 'network',
          message: `Fetch Error: ${error.message}`,
          stack: error.stack,
          url: inputStr,
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
          pathname: window.location.pathname,
          severity: 'medium',
          error,
        };

        addToErrorStack(errorEvent);
        reportError(errorEvent);
        throw error;
      });
  } as typeof window.fetch;
}

/**
 * Get error statistics
 */
export function getErrorStats(): ErrorStats {
  const stats: ErrorStats = {
    totalErrors: errorStack.length,
    bySeverity: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
    },
    byType: {},
    lastError: errorStack[errorStack.length - 1],
    firstErrorTime: errorStack[0]?.timestamp,
  };

  errorStack.forEach((error) => {
    stats.bySeverity[error.severity]++;
    stats.byType[error.type] = (stats.byType[error.type] || 0) + 1;
  });

  return stats;
}

/**
 * Get error history
 */
export function getErrorHistory(limit?: number): ErrorEvent[] {
  if (limit) {
    return errorStack.slice(-limit);
  }
  return [...errorStack];
}

/**
 * Clear error history
 */
export function clearErrorHistory(): void {
  errorStack.length = 0;
}

/**
 * Clear error handlers
 */
export function clearErrorHandlers(): void {
  errorHandlers.length = 0;
}

/**
 * Send errors to external service
 */
export function sendErrorToService(
  errorEvent: ErrorEvent,
  endpoint: string,
  customData?: Record<string, any>
): void {
  try {
    const payload = JSON.stringify({
      ...errorEvent,
      ...customData,
      timestamp: new Date().toISOString(),
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, payload);
    } else {
      fetch(endpoint, {
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
        keepalive: true,
      }).catch((error) => {
        console.error('[ErrorMonitoring] Failed to send error:', error);
      });
    }
  } catch (error) {
    console.error('[ErrorMonitoring] Error sending to service:', error);
  }
}

/**
 * Log errors to console (for development)
 */
export function logErrorToConsole(errorEvent: ErrorEvent): void {
  const colors: Record<string, string> = {
    low: '#3b82f6',
    medium: '#f59e0b',
    high: '#ef4444',
    critical: '#7c3aed',
  };

  console.error(
    `%c[${errorEvent.type.toUpperCase()}] ${errorEvent.message}`,
    `color: ${colors[errorEvent.severity]}; font-weight: bold;`,
    {
      severity: errorEvent.severity,
      tags: errorEvent.tags,
      context: errorEvent.context,
      stack: errorEvent.stack,
      errorEvent,
    }
  );
}
